import isEmpty from 'lodash/isEmpty'
import { IAutocompleteOption } from '../../Autocomplete/types'
import { TIconName } from '../../Autocomplete/utils'
import { IAutoCompleteGroupObject } from '../types'

/**
 * Default values
 */
export const EMPTY_AUTOCOMPLETE_VALUE: IAutocompleteOption = { id: '-3', text: '', groupIndex: 0 }
export const UNASSIGNED_VALUE: IAutocompleteOption = {
  id: '-2',
  text: 'Unassigned',
  groupIndex: 0,
}

export const ADD_OPTION_VALUE: IAutocompleteOption = {
  text: 'Add',
  id: '-1',
  className: 'autocomplete-option-link',
  groupIndex: 0,
}

/**
 * This object is passed to `getAutosuggestOptions()` to create
 * an array of IAutocompleteOption[]
 */
export interface IGetAutocompleteOptionsConfig<T extends Object, M extends T = T> {
  /**
   * {T} type is canonical generic which also sets M
   */
  options: T[]
  /**
   * Default index used for ordering groups in ascending order
   */
  groupIndex: number
  /**
   * Group name is rendered as a label above each group
   * @example: 'Recent customers'
   */
  groupName?: string
  /**
   * Entity name refers to the value used in rendering custom options
   * @example 'New customer'
   */
  entityName?: string
  /**
   * Input value that is used to render custom options
   */
  inputValue?: string
  /**
   * Icon used in rendering each option
   * @example 'Clock'
   */
  iconName?: TIconName
  /**
   * Includes "Add Option"
   * @default true
   */
  includeAddOption?: boolean
  /**
   * Uses just `text` and `id` props to map an array.
   * @note optionally can include any other property from
   * `IAutocompleteOption`, but requires that it be mapped and rendered
   * with a custom `renderOptionType` prop
   * @example
   * ```
   * const mapOptionProp = ({ name: text, id }) => ({
   *   text,
   *   id
   * })
   * ```
   */
  mapOption: (option: M) => Omit<IAutocompleteOption, 'groupIndex'>
}

/**
 * Returns dynamic integer value based on inputValue
 * @example
 * const ex1 = getDynamicGroupIndex(1, 'Has a value') // 2
 * const ex2 = getDynamicGroupIndex(1, undefined) // 0
 */
export const getDynamicGroupIndex = (
  groupIndex: number,
  inputValue?: string,
  incrementValue: number = 1,
  decrementValue: number = 1
): number => {
  return inputValue ? groupIndex + incrementValue : groupIndex - decrementValue
}

/**
 * Returns option text that is computed based on prefix and inputValue
 * @example getDynamicOptionText('Add ', inputValue, 'New Customer')
 */
export const getDynamicOptionText = (
  prefix: string = 'Add ',
  inputValue?: string,
  entityName: string = ''
): string => {
  const entity = inputValue ? `"${inputValue}"` : entityName
  return `${prefix}${entity}`
}

/**
 * Creates an "Add [entity]" option for <Autosuggest />
 * @param {IGetAddOptionArgs} args
 */
export function getAddOption<T>(
  args: Pick<IGetAutocompleteOptionsConfig<T>, 'groupIndex' | 'inputValue' | 'entityName'>
): IAutocompleteOption {
  const { entityName, groupIndex, inputValue } = args || {}
  return {
    id: '-1',
    groupName: undefined,
    className: 'autocomplete-option-link',
    iconName: 'PlusIcon',
    groupIndex: getDynamicGroupIndex(groupIndex, inputValue),
    text: getDynamicOptionText('Add ', inputValue, entityName),
  }
}

/**
 * Compose options array for <Autosuggest />
 * @param {IGetAutocompleteOptionsConfig<T>} config
 * @example
 *  const customerOptions = getAutosuggestOptions({
 *    options: customerList,
 *    groupIndex: 1,
 *    groupName: 'Customers',
 *    iconName: 'GlobeIcon',
 *    entityName: 'New Customer',
 *    inputValue: inputValue,
 *    mapOption: ({ name: text, id = '' }) => ({ text, id }),
 *  })
 */
export function getAutosuggestOptions<T>(
  config: IGetAutocompleteOptionsConfig<T>
): IAutocompleteOption[] {
  const {
    options = [],
    mapOption,
    groupIndex,
    entityName,
    inputValue,
    groupName = '',
    iconName,
    includeAddOption = true,
  } = config || {}

  const mappedOptions: IAutocompleteOption[] = options.map(mapOption).map(o => ({
    ...o,
    groupIndex,
    groupName,
    iconName,
  }))

  // Create 'add option' here
  const addOption = getAddOption({
    groupIndex,
    entityName,
    inputValue,
  })

  if (entityName && includeAddOption) mappedOptions.push(addOption)

  return mappedOptions
}

/**
 * Returns an object, keyed by `groupIndex`, based on `options` values
 * @example
 * {
 *   1: { groupName: 'Customers', groupIndex: 1 },
 *   2: { groupName: 'Dispatchers', groupIndex: 2 }
 * }
 */
export const getOptionGroupDetails = (
  options: IAutocompleteOption[]
): Record<number, IAutoCompleteGroupObject> => {
  const groupsByGroupIndex: Record<number, IAutoCompleteGroupObject> = {}

  for (const option of options) {
    const { className, groupIndex, groupName } = option || {}

    if (isEmpty(groupsByGroupIndex[groupIndex])) {
      groupsByGroupIndex[groupIndex] = {
        groupIndex,
        className,
        groupName,
      }
    }
  }

  return groupsByGroupIndex
}

/**
 * Helper function to add props to option
 * @param {IAutocompleteOption} option - Base option
 * @param {Partial<IAutocompleteOption>} params - Props to add
 */
export const getOptionWithParams = (
  option: IAutocompleteOption,
  params: Partial<IAutocompleteOption>
): IAutocompleteOption => {
  return {
    ...option,
    ...params,
  }
}
